import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import { ASSISTANT_COPY, FROM_ASSISTANT_PARAM, FROM_ASSISTANT_VALUE } from '../../constants/assistantCopy';
import folderService from '../../services/folderService';
import folderD3Service from '../../services/folderD3Service';
import AssistantPage, { AssistantResult } from './AssistantPage';

const KNOWLEDGE_BASES = {
  dim1: {
    permission: PERMISSIONS.KB_DIM1,
    loadFolder: (id) => folderService.getFolderWithFiles(id),
    uploadPath: (id) => `/knowledge-base/folder/${id}/upload`,
  },
  dim3: {
    permission: PERMISSIONS.KB_DIM3,
    loadFolder: (id) => folderD3Service.getFolderWithFiles(id),
    uploadPath: (id) => `/knowledge-base-d3/folder/${id}/upload`,
  },
};

/**
 * `ASSISTANT_UPLOAD_PAGE_URL` → /assistant/upload/{kb}/{folder_id}.
 *
 * Claude can't send PDF or Word files through a tool call, so `get_upload_link`
 * sends the user here. This page only checks the folder is still theirs to see
 * and hands over to the existing upload screen, which ends on "go back to your
 * chat" instead of redirecting into the folder.
 */
const AssistantUploadRedirect = () => {
  const { kb, folderId } = useParams();
  const { has, ready } = usePermissions();
  const target = KNOWLEDGE_BASES[kb];
  const validParams = !!target && /^\d+$/.test(folderId || '');
  // Fails open while permissions load, like every feature gate.
  const permitted = validParams && (!ready || has(target.permission));

  const [state, setState] = useState('checking'); // checking | ok | unavailable | failed

  const check = useCallback(async () => {
    if (!validParams) return;
    setState('checking');
    try {
      await target.loadFolder(folderId);
      setState('ok');
    } catch (err) {
      // 403/404: deleted, or its share was removed — say so plainly rather
      // than opening an upload screen for a folder they can't use.
      setState(err?.status === 403 || err?.status === 404 ? 'unavailable' : 'failed');
    }
  }, [validParams, target, folderId]);

  useEffect(() => {
    if (permitted) check();
  }, [permitted, check]);

  if (validParams && permitted && state === 'ok') {
    return (
      <Navigate
        replace
        to={`${target.uploadPath(folderId)}?${FROM_ASSISTANT_PARAM}=${FROM_ASSISTANT_VALUE}`}
      />
    );
  }

  let content;
  if (!validParams || !permitted || state === 'unavailable') {
    content = (
      <AssistantResult
        tone="error"
        title={ASSISTANT_COPY.folderUnavailable}
        hint={ASSISTANT_COPY.folderUnavailableHint}
      />
    );
  } else if (state === 'failed') {
    content = (
      <AssistantResult
        tone="error"
        title={ASSISTANT_COPY.folderLoadFailed}
        actions={
          <Button variant="contained" onClick={check}>
            Try again
          </Button>
        }
      />
    );
  } else {
    content = <AssistantResult tone="loading" title="Opening the folder…" />;
  }

  return <AssistantPage>{content}</AssistantPage>;
};

export default AssistantUploadRedirect;
